import { PageShell, GetStarted } from '../components';
import { PageBody, PageHero, Section, Steps, SmartLink, Note } from '../components/page';
import { SIGNUP_URL, BREW_APP, BREW_CLI } from '../lib/appUrls';
import { CopyCommand } from '../components/CopyCommand';

/**
 * The setup sequence, in the order a new team does it. Every command here is a
 * real `mcphub` command (.impeccable/product-truth.md). One-click client setup
 * lives in the desktop app or `mcphub config`, never in the web dashboard, so
 * no step may say otherwise. The old step 4 ("one STATIO_TOKEN that expires
 * after 24 hours") was false: access tokens and agent tokens last 5 minutes,
 * refresh 7 days.
 *
 * The desktop app is required: it is how a person's machine signs in, and it
 * ships the mcphub CLI (bundled as a Tauri sidecar in ../Pugio apps/desktop;
 * the CLI is not published on its own). Say so up front, not in step five.
 */
const STEPS = [
  {
    title: 'Create your Statio account',
    body: (
      <p>
        Sign up at <SmartLink href={SIGNUP_URL}>app.statio.dev</SmartLink>. The Free plan needs no card. The web
        dashboard is where you add servers and credentials, grant access, invite your team and read Activity.
      </p>
    ),
  },
  {
    title: 'Install the desktop app',
    body: (
      <>
        <p>
          Every person who uses an AI client with Statio installs the{' '}
          <SmartLink href="/download">desktop app</SmartLink> for macOS, Windows or Linux and signs in with their
          account. It connects that machine to the gateway, keeps its device key in the system keychain where one is
          available, writes the configuration for each AI client, and includes the mcphub CLI. On a Mac you can install
          it with Homebrew:
        </p>
        <CopyCommand text={BREW_APP} />
      </>
    ),
  },
  {
    title: 'Add your credentials',
    body: (
      <p>
        Add the API keys your agents call through, whether that is Stripe, GitHub, Slack or something internal. They are
        encrypted with AES-256-GCM at rest, fetched at call time inside the gateway, and never handed to an agent.
      </p>
    ),
  },
  {
    title: 'Grant access per tool',
    body: (
      <p>
        Turn individual tools on and off rather than whole servers. An agent allowed to read a database is not thereby
        allowed to delete from it, and the gateway checks that on every request before it decrypts anything.
      </p>
    ),
  },
  {
    title: 'Connect your AI clients',
    body: (
      <>
        <p>
          In the desktop app, configure each client from Config in one click, or from the terminal with the mcphub CLI
          that comes with it. Either way it writes the correct MCP configuration for Claude Code, Claude Desktop,
          Cursor, Windsurf, VS Code, Cline, Continue, Zed and Amazon Q, so nobody hand-edits JSON. The web dashboard
          does not configure clients.
        </p>
        <CopyCommand text={'mcphub login\nmcphub config <client>'} />
        <p>To install the CLI on its own, on macOS or Linux:</p>
        <CopyCommand text={BREW_CLI} />
      </>
    ),
  },
  {
    title: 'Give each agent its own identity',
    body: (
      <>
        <p>
          Agents sign in as themselves rather than borrowing a person’s token. Agent tokens last five minutes and the
          agent signs in again with its own credential, so a leaked token is soon useless, and you can suspend or revoke
          the agent from the dashboard within seconds.
        </p>
        <CopyCommand text="mcphub agent login" />
      </>
    ),
  },
  {
    title: 'See what your agents did',
    body: (
      <>
        <p>
          Every tool call is logged in Activity with the identity behind it, a timestamp, the latency and the outcome.
          Audit a machine for MCP servers Statio does not manage, enroll devices to see them across the team in Fleet
          (Team plan and up), and stream the whole record to your SIEM (Business plan and up).
        </p>
        <CopyCommand text={'mcphub audit\nmcphub device enroll\nmcphub fleet'} />
      </>
    ),
  },
];

export default function GettingStartedPage() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Getting started with Statio"
          lede="From a new account to a governed, audited gateway, in a few minutes."
          art="sunrise"
        />
        <Section id="before" title="What you need" split>
          <Note title="A Statio account and the desktop app">
            Statio runs in two places. Your account and the web dashboard hold the servers, credentials and access
            rules; the desktop app connects each person’s machine and AI clients to the gateway. Both are free to start,
            and the desktop app is required.
          </Note>
        </Section>
        <Section id="setup" title="Setup" lede="In the order a new team does it." split>
          <Steps items={STEPS} />
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
